import React, { useState } from 'react';
import { Select, Space } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../../i18n/config';
import { languageService } from '../../../services/localeService';
import './LanguageSwitcher.css';

const { Option } = Select;

const LANGUAGE_MARKERS: Record<SupportedLanguage, string> = {
  'zh-CN': 'CN',
  'zh-HK': 'HK',
  en: 'EN',
};

export interface LanguageOption {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  flag?: string;
}

export interface LanguageSwitcherProps {
  placement?: 'header' | 'footer' | 'sidebar' | 'login';
  size?: 'small' | 'middle' | 'large';
  showFlags?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  placement = 'header',
  size = 'middle',
  showFlags = false,
  style,
  className,
}) => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleLanguageChange = async (languageCode: SupportedLanguage) => {
    setIsLoading(true);
    try {
      await languageService.changeLanguage(languageCode);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentLanguage = SUPPORTED_LANGUAGES[i18n.language as SupportedLanguage]
    ? (i18n.language as SupportedLanguage)
    : 'zh-CN';

  const languageOptions = Object.values(SUPPORTED_LANGUAGES).map((lang) => ({
    code: lang.code,
    name: lang.name,
    nativeName: lang.nativeName,
    flag: LANGUAGE_MARKERS[lang.code],
  }));

  const getSelectStyle = () => {
    const baseStyle: React.CSSProperties = {
      minWidth: 120,
      ...style,
    };

    if (placement === 'header') {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        color: '#fff',
      };
    }

    if (placement === 'login') {
      return {
        ...baseStyle,
        minWidth: 78,
        color: '#0f172a',
      };
    }

    return baseStyle;
  };

  const switcherClassName = [
    'language-switcher',
    `language-switcher--${placement}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const popupClassName =
    placement === 'header'
      ? 'language-switcher-header-dropdown'
      : placement === 'login'
        ? 'language-switcher-login-dropdown'
        : undefined;
  const popupContainer =
    placement === 'login'
      ? () => document.body
      : undefined;
  const iconColor =
    placement === 'header'
      ? '#fff'
      : placement === 'login'
        ? '#475569'
        : undefined;

  return (
    <Space align="center" className={switcherClassName}>
      <GlobalOutlined
        style={{
          color: iconColor,
          fontSize: size === 'small' ? 14 : size === 'large' ? 18 : 16,
        }}
      />
      <Select
        value={currentLanguage}
        onChange={handleLanguageChange}
        size={size}
        style={getSelectStyle()}
        popupMatchSelectWidth={placement === 'login' ? false : undefined}
        suffixIcon={null}
        variant={placement !== 'header' ? 'outlined' : 'borderless'}
        loading={isLoading}
        aria-label="Language Switcher"
        data-testid="language-switcher"
        popupClassName={popupClassName}
        getPopupContainer={popupContainer}
      >
        {languageOptions.map((option) => (
          <Option
            key={option.code}
            value={option.code}
            data-testid={`language-option-${option.code}`}
          >
            <Space>
              {showFlags && option.flag && <span>{option.flag}</span>}
              <span>{option.nativeName}</span>
            </Space>
          </Option>
        ))}
      </Select>
    </Space>
  );
};

export default LanguageSwitcher;
